import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { getSimpleListSubsidiary } from "../../crud/customer.crud";
import { actionTypes as authActionTypes } from "./auth.duck";

/**
 * Unidade (matriz ou filial) escolhida no seletor da navegação.
 *
 * A escolha vale para o painel inteiro: o axios manda a unidade em toda requisição (header `x-unidade`) e o
 * backend restringe o que cada consulta enxerga. Na própria unidade a matriz segue enxergando o grupo;
 * escolhendo uma filial, tudo passa a ser dela e o painel fica somente leitura.
 */

export const actionTypes = {
    UnitsLoading: "[Loading Units] Action",
    UnitsLoaded: "[Load Units] Customer API",
    UnitSelected: "[Select Unit] Action",
};

const initialUnitState = {
    units: [],
    ownUnitId: null,
    isParent: true,
    selectedUnit: null,
    loading: false,
};

// Só a escolha é persistida: a lista de unidades é buscada na API a cada sessão.
export const reducer = persistReducer({ storage, key: "app-unit", whitelist: ["selectedUnit"] }, (state = initialUnitState, action) => {
    switch (action.type) {
        case actionTypes.UnitsLoading: {
            return { ...state, loading: action.payload.loading };
        }

        case actionTypes.UnitsLoaded: {
            const { units, ownUnitId, isParent } = action.payload;

            // A escolha guardada da sessão anterior só continua valendo se a unidade ainda estiver disponível;
            // sem escolha, o painel abre na unidade do próprio usuário.
            const selectedUnit = units.find((unit) => unit.id === state.selectedUnit?.id) ?? units.find((unit) => unit.id === ownUnitId) ?? null;

            return { ...state, units, ownUnitId, isParent, selectedUnit, loading: false };
        }

        case actionTypes.UnitSelected: {
            return { ...state, selectedUnit: action.payload.unit };
        }

        case authActionTypes.Login:
        case authActionTypes.Logout:
        case authActionTypes.Forbidden: {
            return initialUnitState;
        }

        default:
            return state;
    }
});

export const actions = {
    setUnitsLoading: (loading = true) => ({ type: actionTypes.UnitsLoading, payload: { loading } }),
    fulfillUnits: (payload) => ({ type: actionTypes.UnitsLoaded, payload }),
    selectUnit: (unit) => ({ type: actionTypes.UnitSelected, payload: { unit } }),
};

let pendingRequest = null;

export const loadUnits = async (dispatch) => {
    dispatch(actions.setUnitsLoading());

    if (!pendingRequest) {
        pendingRequest = getSimpleListSubsidiary().finally(() => {
            pendingRequest = null;
        });
    }

    try {
        const { data } = await pendingRequest;

        if (data.errors) {
            throw new Error(data.errors[0]);
        }

        const { company, subsidiaries, is_parent } = data;

        dispatch(
            actions.fulfillUnits({
                units: is_parent ? [company, ...(subsidiaries ?? [])] : [company],
                ownUnitId: company?.id ?? null,
                isParent: is_parent,
            }),
        );
    } catch (err) {
        dispatch(actions.setUnitsLoading(false));

        throw err;
    }
};

/** Visualizando outra unidade o painel é somente leitura: os dados são da filial, não de quem está olhando. */
export const isReadOnly = ({ selectedUnit, ownUnitId }) => Boolean(selectedUnit && ownUnitId && selectedUnit.id !== ownUnitId);
